import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, ExternalLink } from "lucide-react";
import SEO from "@/components/SEO";
import { PageHero, CTABand } from "@/components/PageHero";
import { Section, SectionHeading, WarningBox, InfoBox } from "@/components/Primitives";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useSite } from "@/context/SiteContext";
import { PROCESS_STEPS, LABUAN_FAQS, OFFICIAL_RESOURCES } from "@/data/content";

const LABUAN_IMG = "https://images.unsplash.com/photo-1780362507310-18e61f3597db?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const CHARACTERISTICS = [
  "At least one director", "At least one shareholder", "Resident secretary",
  "Registered office in Labuan", "Incorporation through a licensed Labuan trust company",
  "No bearer shares", "Activity-specific licensing where required", "Annual compliance requirements",
];
const SUBSTANCE = ["A physical office in Labuan", "Appropriate personnel", "Appropriate operating activity", "Proper records and management", "Activity-specific compliance"];
const NORMAL = ["International trading", "Consulting", "Digital services", "E-commerce", "Holding activities", "Regional management", "Import and export"];
const REGULATED = ["Banking", "Insurance", "Leasing", "Factoring", "Fund management", "Money broking", "Securities", "Other financial services"];
const DOCS = ["Passport copy", "Proof of residential address", "Professional or business profile", "Proposed company names", "Description of business activity", "Shareholder and director information", "Source-of-funds information", "Business plan, where required", "Corporate documents for corporate shareholders", "Additional due-diligence documents when requested"];

function Block({ id, title, children }) {
  return (
    <div id={id} className="scroll-mt-28">
      <h2 className="text-xl md:text-2xl font-bold text-navy">{title}</h2>
      <div className="mt-3 text-slate-600 leading-relaxed space-y-3">{children}</div>
    </div>
  );
}
const Tick = ({ children }) => (
  <li className="flex items-start gap-3 text-sm text-navy"><span className="w-5 h-5 rounded-full bg-emerald-brand/10 flex items-center justify-center shrink-0 mt-0.5"><Check size={12} className="text-emerald-brand" /></span>{children}</li>
);

export default function LabuanPage() {
  const { openWhatsApp } = useSite();
  const faqJsonLd = {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: LABUAN_FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };

  return (
    <>
      <SEO title="Labuan Company Setup for Foreigners"
        description="Set up your international business in Labuan, Malaysia. Structured support for foreign entrepreneurs, international companies and investors. Contact us to know more."
        path="/labuan-company-setup" jsonLd={faqJsonLd} />

      <div data-testid="labuan-hero-section">
        <PageHero eyebrow="Primary Advisory" image={LABUAN_IMG}
          title="Set Up Your International Business in Labuan, Malaysia"
          subtitle="Structured support for foreign entrepreneurs, international companies and investors considering Labuan IBFC. Contact us to know more.">
          <Link to="/eligibility" className="btn-gold rounded-full px-7 py-3.5 font-bold inline-flex items-center gap-2">Request Labuan Assessment <ArrowRight size={18} /></Link>
          <button onClick={() => openWhatsApp({ service: "Labuan company setup", serviceKey: "labuan" })} className="rounded-full px-7 py-3.5 font-bold bg-white/10 border-2 border-white/30 hover:bg-white/20 transition-colors">Speak to a Consultant</button>
        </PageHero>
      </div>

      <Section className="bg-white">
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-12">
            <Block id="what-is-labuan" title="What Is Labuan IBFC?">
              <p>Labuan is a Malaysian federal territory and an international business and financial centre. It provides a credible, regulated framework for entrepreneurs and companies that operate across borders and want a recognised base within Asia.</p>
              <p>A Labuan company can be used for a range of international business activities, always subject to the proposed activity, due diligence and the applicable regulatory requirements.</p>
              <p><Link to="/contact" className="text-emerald-brand font-semibold">Contact us to know more.</Link></p>
            </Block>

            <Block id="who-can-establish" title="Who Can Establish a Labuan Company?">
              <p>Both Malaysian residents and non-residents may establish a Labuan company. Eligibility is subject to the proposed activity, due diligence and applicable rules at the time of incorporation.</p>
              <p><Link to="/contact" className="text-emerald-brand font-semibold">Contact us to know more.</Link></p>
            </Block>

            <Block id="characteristics" title="Main Characteristics">
              <ul className="grid sm:grid-cols-2 gap-3">{CHARACTERISTICS.map((c) => <Tick key={c}>{c}</Tick>)}</ul>
              <InfoBox>Contact us to know more about the applicable requirements.</InfoBox>
            </Block>

            <Block id="trading" title="Trading and Non-Trading Activities">
              <p>In plain terms, a Labuan trading activity generally involves active commercial operations such as trading, consulting or services, while a non-trading activity generally involves holding investments such as shares, securities or property. The distinction affects how an activity is treated. We do not provide individual tax advice.</p>
              <p><Link to="/contact" className="text-emerald-brand font-semibold">Contact us to know more.</Link></p>
            </Block>

            <Block id="tax" title="Tax Overview">
              <p>Labuan trading activity may receive specific tax treatment under applicable Labuan legislation, while qualifying non-trading activity may receive different treatment. This is subject to current legislation, substantial activity requirements and full compliance.</p>
              <p>Tax outcomes depend on the actual activity, management, substance, source of income, ownership, applicable treaties and the tax residence of the owners. Independent tax advice is required. <Link to="/contact" className="text-emerald-brand font-semibold">Contact us to know more.</Link></p>
            </Block>

            <Block id="substance" title="Substance Requirements">
              <p>Access to Labuan tax treatment may require appropriate operational substance, which can include:</p>
              <ul className="grid sm:grid-cols-2 gap-3">{SUBSTANCE.map((s) => <Tick key={s}>{s}</Tick>)}</ul>
              <p>Requirements vary by business activity. <Link to="/contact" className="text-emerald-brand font-semibold">Contact us to know more about the requirements relevant to your proposed activity.</Link></p>
            </Block>

            <Block id="banking" title="Banking Support">
              <p>Global Pass Consultants can help prepare the application and coordinate introductions.</p>
              <WarningBox>Bank account approval depends on the bank's KYC process, source-of-funds review, business activity, ownership structure and risk policy. No bank account is guaranteed. Contact us to know more.</WarningBox>
            </Block>

            <Block id="work-permits" title="Work Permits and Family">
              <p>Qualifying Labuan entities may apply for work permits for eligible directors or employees. Availability, duration, role requirements, salary requirements and dependent options are subject to current Labuan FSA and Malaysian immigration rules. <Link to="/contact" className="text-emerald-brand font-semibold">Contact us to know more.</Link></p>
              <WarningBox>Company incorporation does not by itself guarantee work permits, dependent visas, residency or automatic immigration. Immigration matters are handled by authorized professionals.</WarningBox>
            </Block>

            <Block id="industries" title="Industries">
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="rounded-xl border border-slate-100 p-5 bg-slate-50/60">
                  <h3 className="font-bold text-navy mb-3">Normal activities</h3>
                  <ul className="space-y-2">{NORMAL.map((n) => <Tick key={n}>{n}</Tick>)}</ul>
                </div>
                <div className="rounded-xl border border-gold/40 p-5 bg-[#fdfaf0]">
                  <h3 className="font-bold text-navy mb-3">Regulated activities</h3>
                  <ul className="space-y-2">{REGULATED.map((r) => <Tick key={r}>{r}</Tick>)}</ul>
                </div>
              </div>
              <p>Regulated activities need proper licensing and professional review. <Link to="/contact" className="text-emerald-brand font-semibold">Contact us to know more.</Link></p>
            </Block>

            <Block id="documents" title="General Document Checklist">
              <ul className="grid sm:grid-cols-2 gap-3">{DOCS.map((d) => <Tick key={d}>{d}</Tick>)}</ul>
              <InfoBox>Contact us to know more about the documents relevant to your circumstances.</InfoBox>
            </Block>

            <Block id="process" title="Setup Process">
              <div className="space-y-3">
                {PROCESS_STEPS.map((s, i) => (
                  <div key={s.title} className="flex gap-4 rounded-xl border border-slate-100 p-4">
                    <div className="text-gold font-extrabold text-2xl shrink-0">{String(i + 1).padStart(2, "0")}</div>
                    <div><div className="font-bold text-navy">{s.title}</div><div className="text-sm text-slate-500">{s.text}</div></div>
                  </div>
                ))}
              </div>
              <p className="text-sm text-slate-500">No costs or fixed timelines are shown. <Link to="/contact" className="text-emerald-brand font-semibold">Contact us to know more.</Link></p>
            </Block>

            <Block id="requirements-note" title="Requirements & Considerations">
              <InfoBox>Every business structure is different. Requirements depend on the proposed activity, ownership, licensing, compliance and operational needs. Contact us to know more.</InfoBox>
            </Block>

            <Block id="faqs" title="Frequently Asked Questions">
              <Accordion type="single" collapsible className="w-full">
                {LABUAN_FAQS.map((f, i) => (
                  <AccordionItem key={f.q} value={`faq-${i}`} data-testid={`labuan-faq-${i}`}>
                    <AccordionTrigger className="text-left text-navy font-semibold">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Block>

            <Block id="resources" title="Official Resources">
              <ul className="space-y-2">
                {OFFICIAL_RESOURCES.map((r) => (
                  <li key={r.url}><a href={r.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-emerald-brand font-semibold hover:underline">{r.label} <ExternalLink size={14} /></a></li>
                ))}
              </ul>
            </Block>
          </div>

          {/* Sticky sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-28 space-y-4">
              <div className="rounded-2xl bg-navy text-white p-6">
                <h3 className="font-bold text-lg">Request a Labuan Assessment</h3>
                <p className="mt-2 text-sm text-slate-300">Every structure is different. The appropriate approach depends on your business and circumstances.</p>
                <Link to="/eligibility" className="mt-4 block text-center btn-gold rounded-full px-5 py-3 font-bold">Start Assessment</Link>
                <button onClick={() => openWhatsApp({ service: "Labuan company setup", serviceKey: "labuan" })} className="mt-2 w-full rounded-full px-5 py-3 font-semibold bg-emerald-brand text-white">Speak on WhatsApp</button>
              </div>
              <div className="rounded-2xl border border-slate-100 p-5 bg-white">
                <h4 className="font-bold text-navy text-sm mb-3">On this page</h4>
                <ul className="space-y-2 text-sm text-slate-500">
                  {["What Is Labuan IBFC?","Who Can Establish","Main Characteristics","Tax Overview","Substance","Banking","Work Permits","Industries","Documents","Process","FAQs"].map((t) => (
                    <li key={t} className="hover:text-emerald-brand cursor-default">{t}</li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </Section>

      <CTABand title="Ready to Explore Labuan?" service="Labuan company setup" serviceKey="labuan" />
    </>
  );
}
